import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import Toast from '@/components/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { ArticleReviewSummary } from '@/features/admin/components/ArticleReviewSummary';
import { ArticleCreate } from './ArticleCreate';
import { DictionaryReview } from './DictionaryReview';

type MenuTab = 'create' | 'summary' | 'words' | 'dictionary' | 'translation' | 'tts' | 'publish';

export const ManagerArticle: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<MenuTab>('create');
  const location = useLocation();
  const [notification, setNotification] = useState<{
    message: string;
    type?: 'success' | 'error' | 'info';
  } | null>(null);

  useEffect(() => {
    const navState: any = (location.state as any) ?? null;
    if (!navState) return;

    const notificationPayload = navState?.notification;
    const requestedTab = navState?.activeTab as MenuTab | undefined;

    if (notificationPayload) {
      setNotification(notificationPayload);
      // auto-dismiss after 3s
      const t = window.setTimeout(() => setNotification(null), 3000);
      // Remove navigation state without triggering a React Router navigation
      try {
        const url = window.location.pathname + window.location.search + window.location.hash;
        window.history.replaceState({}, '', url);
      } catch (e) {
        // ignore
      }
      if (requestedTab) setActiveTab(requestedTab);
      return () => window.clearTimeout(t);
    }

    if (requestedTab) {
      setActiveTab(requestedTab);
      try {
        const url = window.location.pathname + window.location.search + window.location.hash;
        window.history.replaceState({}, '', url);
      } catch (e) {
        // ignore
      }
    }
  }, [location]);

  const menuItems: { id: MenuTab; label: string; icon: string }[] = [
    { id: 'create', label: 'Create Article', icon: '📝' },
    { id: 'summary', label: 'Article Summary', icon: '📊' },
    { id: 'words', label: 'Extracted Words', icon: '✂️' },
    { id: 'translation', label: 'Translation', icon: '🌐' },
    { id: 'tts', label: 'Text-to-Speech', icon: '🔊' },
    { id: 'publish', label: 'Publish Article', icon: '✅' },
    { id: 'dictionary', label: 'Dictionary', icon: '📖' },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Manage Articles</h1>
          <p className="text-gray-600 mt-2">
            Create, review, publish articles and manage Dictionary
          </p>
        </div>
        {user?.role === 'admin' && (
          <Button variant="outline" onClick={() => navigate('/admin/dashboard')}>
            ← Back to Dashboard
          </Button>
        )}
      </div>

      <div className="flex gap-6">
        {/* Left Sidebar Menu - Narrow width */}
        <div style={{ width: '300px', flexShrink: 0 }}>
          <Card className="p-4 sticky top-4">
            <div className="space-y-2">
              {user?.role === 'admin' && (
                <button
                  onClick={() => navigate('/editor/articles/plans')}
                  className="w-full text-left px-4 py-3 rounded-lg transition-colors text-sm bg-amber-100 text-amber-900 hover:bg-amber-200"
                >
                  <span className="mr-2">🗂️</span>
                  <span>Article Plan</span>
                </button>
              )}
              {user?.role === 'admin' && (
                <button
                  onClick={() => navigate('/editor/articles/create-batch')}
                  className="w-full text-left px-4 py-3 rounded-lg transition-colors text-sm bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                >
                  <span className="mr-2">📚</span>
                  <span>Create Batch Articles</span>
                </button>
              )}
              {menuItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-colors text-sm ${
                    activeTab === item.id
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="mr-2">{item.icon}</span>
                  <span className={activeTab === item.id ? 'text-blue-800' : ''}>{item.label}</span>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Main Content - Takes remaining space */}
        <div className="flex-1">
          {/* Notification toast from navigation state */}
          <Toast notification={notification} onClose={() => setNotification(null)} />
          {/* Tab Content */}
          {activeTab === 'create' && (
            <>
              <ArticleCreate />
            </>
          )}
          {activeTab === 'summary' && (
            <>
              <ArticleReviewSummary isSummaryReviewed={true} />
            </>
          )}
          {activeTab === 'words' && (
            <>
              <ArticleReviewSummary isExtractedWordsReviewed={true} />
            </>
          )}

          {activeTab === 'translation' && (
            <>
              <ArticleReviewSummary isTranslationReviewed={true} />
            </>
          )}
          {activeTab === 'tts' && (
            <>
              <ArticleReviewSummary isTtsReviewed={true} />
            </>
          )}
          {activeTab === 'publish' && (
            <>
              <ArticleReviewSummary isPublishArticleTab={true} />
            </>
          )}
          {activeTab === 'dictionary' && <DictionaryReview />}
        </div>
      </div>
    </div>
  );
};
